import { useEffect, useState } from "react";
import api, { fileUrl } from "../../lib/api";
import { Plus, Trash2, Pencil, Save, X, Upload as UploadIcon, Star, ExternalLink, Building2, Copy } from "lucide-react";

const TIERS = ["Donor", "Government", "UN Agency", "Partner", "Implementing Partner", "Other"];
const empty = { name: "", tier: "Partner", description: "", logo_url: "", website: "", order: 0, featured: false, published: true };

export default function PartnersEditor() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/partners?all=true").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const save = async (p) => {
        const payload = { ...p, order: parseInt(p.order) || 0 };
        if (p.id) await api.put(`/partners/${p.id}`, payload);
        else await api.post("/partners", payload);
        setEditing(null); load();
    };
    const del = async (id) => { if (!window.confirm("Delete partner?")) return; await api.delete(`/partners/${id}`); load(); };
    const dup = async (id) => { await api.post(`/partners/${id}/duplicate`); load(); };

    return (
        <div data-testid="partners-editor">
            <div className="flex items-center justify-between mb-8">
                <div><h1 className="font-display text-4xl mb-2">Partners & Donors</h1><p className="text-muted-foreground">Manage the partners shown on the public Partners page.</p></div>
                <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium" data-testid="add-partner">
                    <Plus size={14} /> New Partner
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((p) => (
                    <div key={p.id} className="bg-card border border-border rounded-sm overflow-hidden" data-testid={`partner-row-${p.id}`}>
                        <div className="aspect-[3/2] bg-background border-b border-border flex items-center justify-center p-6 relative">
                            {p.featured && <Star size={14} className="absolute top-2 right-2 fill-primary text-primary" />}
                            {p.logo_url ? <img src={fileUrl(p.logo_url)} alt={p.name} className="max-h-full max-w-full object-contain" /> : <Building2 size={36} className="text-muted-foreground/40" />}
                        </div>
                        <div className="p-4">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-primary mb-1">{p.tier}{!p.published && <span className="ml-2 text-muted-foreground">(unpublished)</span>}</div>
                            <h3 className="font-display text-lg leading-tight mb-1">{p.name}</h3>
                            {p.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{p.description}</p>}
                            <div className="flex gap-2 mt-3">
                                <button onClick={() => setEditing({ ...p })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs"><Pencil size={11} /> Edit</button>
                                <button onClick={() => dup(p.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs" data-testid={`dup-partner-${p.id}`}><Copy size={11} /> Duplicate</button>
                                <button onClick={() => del(p.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-destructive text-destructive text-xs"><Trash2 size={11} /> Delete</button>
                                {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs ml-auto"><ExternalLink size={11} /></a>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && <Modal item={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />}
        </div>
    );
}

const Modal = ({ item, onChange, onSave, onClose }) => {
    const set = (k, v) => onChange({ ...item, [k]: v });
    const upload = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        set("logo_url", data.url);
    };
    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="partner-modal">
            <div className="bg-card border border-border rounded-sm w-full max-w-2xl my-10">
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="font-display text-2xl">{item.id ? "Edit Partner" : "New Partner"}</h2>
                    <button onClick={onClose} className="p-2 rounded-sm hover:bg-muted"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <F label="Name *" value={item.name} onChange={(v) => set("name", v)} testid="partner-name" />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Tier / Category</span>
                            <select value={item.tier} onChange={(e) => set("tier", e.target.value)} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="partner-tier">
                                {TIERS.map((t) => <option key={t}>{t}</option>)}
                            </select>
                        </label>
                        <F label="Order" type="number" value={item.order} onChange={(v) => set("order", v)} testid="partner-order" />
                    </div>
                    <T label="Description" value={item.description} onChange={(v) => set("description", v)} testid="partner-desc" />
                    <F label="Website URL" value={item.website} onChange={(v) => set("website", v)} testid="partner-website" />
                    <div>
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Logo</span>
                        <div className="flex items-center gap-3 mb-3">
                            <input value={item.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://… or /api/files/…"
                                className="flex-1 px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="partner-logo-url" />
                            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-border cursor-pointer text-sm whitespace-nowrap">
                                <UploadIcon size={14} /> Upload
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="partner-logo-upload" />
                            </label>
                        </div>
                        {item.logo_url && <img src={fileUrl(item.logo_url)} alt="" className="h-20 border border-border rounded-sm bg-background object-contain p-2" />}
                    </div>
                    <div className="flex gap-6">
                        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!item.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="partner-featured" /><span className="text-sm">Featured</span></label>
                        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!item.published} onChange={(e) => set("published", e.target.checked)} data-testid="partner-published" /><span className="text-sm">Published</span></label>
                    </div>
                </div>
                <div className="p-6 border-t border-border flex justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 rounded-sm border border-border text-sm">Cancel</button>
                    <button onClick={() => onSave(item)} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium" data-testid="partner-save"><Save size={14} /> Save</button>
                </div>
            </div>
        </div>
    );
};

const F = ({ label, value, onChange, testid, type = "text" }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" /></label>
);
const T = ({ label, value, onChange, testid }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm resize-y" /></label>
);
